// Currency presentation for the operator console — the SM-443 contract
// consumed, not forked.
//
// Since SM-396 phase 06b the contract itself lives in `src/currency`,
// ported byte-for-byte from web-client and pinned by the three-app
// parity fixture. This module is the console's thin adapter over it:
// the codes, the seed table and the fallback rules all come from
// `@/currency` so there is exactly ONE seed in the app, and what
// remains here is operator-console-specific — the legacy-payload
// fallback and the amount-entry unit.
//
// VALUE stays in src/lib/money.ts (int64 minor units, truncate never
// round); presentation only answers "what does the operator see next to
// the number?". Since SM-460 that answer is operator CONFIGURATION for
// every currency including GOLD — name, symbol, colour, decimals and
// artwork all come from the /currencies page, and GOLD no longer
// inherits the ⚡ brand glyph it used to be pinned to.

import { SPARK_UNIT_MINOR, parseSparkToMinor } from "@/lib/money";
import {
  CURRENCY_CODES as CONTRACT_CODES,
  isCurrencyCode as contractIsCurrencyCode,
  monogramGlyph as contractMonogramGlyph,
  resolveCurrencyPresentation,
  type CurrencyCode as ContractCurrencyCode,
} from "@/currency";

export const CURRENCY_CODES = CONTRACT_CODES;
export type CurrencyCode = ContractCurrencyCode;

export interface CurrencySeed {
  displayName: string;
  symbol: string;
  color: string;
}

/**
 * Frozen seed — resolved from the ported contract with no operator
 * settings, which is the definition of "seeded". Deriving it (rather
 * than repeating the literals) is what keeps this console's unconfigured
 * RED identical to web-client's: there is no second table to forget to
 * update.
 */
export const CURRENCY_SEED: Readonly<Record<CurrencyCode, CurrencySeed>> = Object.freeze(
  Object.fromEntries(
    CURRENCY_CODES.map((code) => {
      const p = resolveCurrencyPresentation(code);
      return [code, Object.freeze({ displayName: p.displayName, symbol: p.symbol, color: p.color })];
    }),
  ) as Record<CurrencyCode, CurrencySeed>,
);

export const isCurrencyCode = contractIsCurrencyCode;

/**
 * Campaign rows predating the 0003 backfill (or any legacy payload)
 * omit the currency — that means the legacy single currency, GOLD
 * (G3 at the presentation boundary only).
 */
export function campaignCurrency(code: string | undefined | null): CurrencyCode {
  return isCurrencyCode(code) ? code : "GOLD";
}

/** Monogram glyph — first two characters of the symbol, uppercased. The
 *  degrade path for a broken configured image only; since SM-491 no
 *  currency seeds a monogram, so this is not what an operator normally sees. */
export const monogramGlyph = contractMonogramGlyph;

/**
 * A wire amount rendered in the currency's ENTRY unit for `code`, as a
 * bare number string — what an operator types, not what they read.
 *
 * The unit comes from the SM-443 presentation contract (`unitMinor`),
 * which is the ONE place any app decides what one unit of a currency
 * is. Since SM-460 that is the same $1 unit for all three (1 unit =
 * wire 10 000): SM-484 had left GOLD on the legacy SPARK entry unit
 * (1 SPARK = 1 cent = wire 100), which is why an operator could type
 * "500" into a transfer box and move $5.
 *
 * Pairs with `parseUnitToMinor` below; the two are inverses, and they
 * are here together so a change to one cannot silently miss the other.
 * That pairing is the whole point: a form whose display unit and parse
 * unit disagree turns "500" into "50,000" on a round trip, and money
 * forms round-trip constantly (open, edit one field, save).
 */
export function unitFromMinor(minor: number | null | undefined, code: CurrencyCode): string {
  if (minor === undefined || minor === null) return "";
  return String(minor / unitMinorFor(code));
}

/**
 * Inverse of `unitFromMinor`: an operator-typed amount → wire minor units.
 *
 * `parseSparkToMinor` already shifts a typed amount by the module's own
 * display unit (SPARK_UNIT_MINOR); the ratio below re-expresses that in
 * THIS currency's unit. It is 1 for all three currencies today, and it is
 * written as a ratio rather than dropped so that a future currency with a
 * different unit cannot silently inherit GOLD's.
 */
export function parseUnitToMinor(input: string, code: CurrencyCode): number {
  const base = Number(parseSparkToMinor(input)); // input × SPARK_UNIT_MINOR, comma-tolerant
  return base * (unitMinorFor(code) / SPARK_UNIT_MINOR);
}

/**
 * Wire minor units per displayed/entered unit for `code`, from the
 * contract's seed. Not exported: every caller should be going through
 * the pair above or through <CurrencyAmount>, and re-exporting a raw
 * divisor is how this console grew a second one last time.
 */
function unitMinorFor(code: CurrencyCode): number {
  return resolveCurrencyPresentation(code).unitMinor;
}
